import { gql } from '@apollo/client';

/**
 * Type for EQPortfolio_set_input GraphQL input.
 * IMPORTANT: fund_type must be object | null, never a string.
 * Storing fund_type as a string crashes the analysis process.
 */
export interface PortfolioSetInput {
  name?: string;
  fund_code?: string;
  inception_date?: string | null;
  base_currency?: string;
  status?: string;
  fund_type?: object | null;
  aum?: number | null;
  benchmark_index?: string | null;
  updatedAt?: string;
}

/** Stamps `updatedAt` with the current ISO timestamp on any portfolio changes object. */
export const withUpdatedAt = <T extends PortfolioSetInput>(changes: T): T => ({
  ...changes,
  updatedAt: new Date().toISOString(),
});

export const GET_PORTFOLIO_DATA = gql`
  query GetPortfolioData($snapshotId: uuid!) {
    EQPortfolioAllocation(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { current_percent: desc }
    ) {
      isin
      current_percent
      proposed_percent
      change_type
      EQInstrument {
        full_ticker
        name
        sector
      }
    }
    EQPortfolioSnapshot(where: { id: { _eq: $snapshotId } }) {
      aum
      total_positions
    }
    EQPortfoliosAIAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
    }
  }
`;

export const GET_PROPOSED_PORTFOLIO_STATS = gql`
  query GetProposedPortfolioStats($snapshotId: uuid!) {
    EQPortfoliosAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      var_30d_95
      sharpe_ratio
      analysis_data
    }
  }
`;

export const GET_AI_RECOMMENDATIONS = gql`
  query GetAIRecommendations($snapshotId: uuid!) {
    EQPortfoliosAIAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
      createdAt
    }
  }
`;

export const GET_AI_ANALYSIS = gql`
  query GetAIAnalysis($snapshotId: uuid!, $sectors: [String!]) {
    EQPortfoliosAIAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
      createdAt
    }

    EQSectorNews(
      where: { sectorname: { _in: $sectors } }
      order_by: { datetime: desc }
      limit: 4
    ) {
      title
      sectorname
      sentiment_analysis
    }
  }
`;

export const GET_AI_ANALYSIS_WITH_PORTFOLIO_ID = gql`
  query GetAIAnalysisWithPortfolioId(
    $snapshotId: uuid!
    $portfolioId: uuid!
    $sectors: [String!]
  ) {
    EQPortfoliosAIAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
      createdAt
    }

    EQSectorNews(
      where: { portfolio_id: { _eq: $portfolioId } }
      order_by: { datetime: desc }
      limit: 4
    ) {
      title
      sectorname
      sentiment_analysis
    }
  }
`;

export const GET_AI_ANALYSIS_ALL_SECTORS = gql`
  query GetAIAnalysisAllSectors($snapshotId: uuid!) {
    EQPortfoliosAIAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
      createdAt
    }

    EQSectorNews(order_by: { datetime: desc }, limit: 4) {
      title
      sectorname
      sentiment_analysis
    }
  }
`;

export const GET_EXPECTED_IMPACT = gql`
  query GetExpectedImpact($snapshotId: uuid!) {
    EQPortfoliosAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
    }
  }
`;

export const GET_RECOMMENDATION_REPORT = gql`
  query GetRecommendationReport($snapshotId: uuid!) {
    EQPortfoliosAIAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
      createdAt
    }
  }
`;

export const GET_RISK_METRICS_COMPARISON = gql`
  query GetRiskMetricsComparison($snapshotId: uuid!) {
    EQPortfoliosAnalysis(
      where: { snapshot_id: { _eq: $snapshotId } }
      order_by: { datetime: desc }
      limit: 1
    ) {
      analysis_data
    }
  }
`;

export const GET_LATEST_SNAPSHOT_ID = gql`
  query GetLatestSnapshotId($portfolioId: uuid) {
    EQPortfolio(where: { id: { _eq: $portfolioId } }) {
      EQPortfolioSnapshots(order_by: { datetime: desc }, limit: 1) {
        id
        datetime
      }
    }
  }
`;

export const GET_PORTFOLIO_NAME = gql`
  query GetPortfolioName($portfolioId: uuid!) {
    EQPortfolio(where: { id: { _eq: $portfolioId } }) {
      id
      name
      fund_code
      base_currency
      inception_date
      fund_type
      aum
      benchmark_index
    }
  }
`;

export const GET_ALL_PORTFOLIOS = gql`
  query GetAllPortfolios {
    EQPortfolio(order_by: { name: asc }) {
      name
      id
      companyId
      createdAt
      fund_code
      fund_type
      inception_date
      base_currency
    }
  }
`;

export const GET_PORTFOLIO_BY_USER_ID = gql`
  query GetPortfolioByUserId($userId: String!) {
    EQPortfolio(where: { user_id: { _eq: $userId } }, order_by: { name: asc }) {
      id
      name
      fund_code
      base_currency
      inception_date
      fund_type
      aum
      benchmark_index
      companyId
      createdAt
      user_id
    }
  }
`;

export const CREATE_PORTFOLIO = gql`
  mutation CreatePortfolio($portfolio: EQPortfolio_insert_input!) {
    insert_EQPortfolio_one(object: $portfolio) {
      id
      name
      fund_code
      inception_date
      base_currency
      benchmark_index
      status
      fund_type
      aum
      user_id
      companyId
      createdAt
    }
  }
`;

export const UPDATE_PORTFOLIO = gql`
  mutation UpdatePortfolio($id: uuid!, $changes: EQPortfolio_set_input!) {
    update_EQPortfolio_by_pk(pk_columns: { id: $id }, _set: $changes) {
      id
      name
      fund_code
      inception_date
      base_currency
      benchmark_index
      status
      fund_type
      aum
      updatedAt
    }
  }
`;

export const UPDATE_PORTFOLIO_SNAPSHOT = gql`
  mutation UpdatePortfolioSnapshot(
    $snapshotId: uuid!
    $changes: EQPortfolioSnapshot_set_input!
  ) {
    update_EQPortfolioSnapshot_by_pk(
      pk_columns: { id: $snapshotId }
      _set: $changes
    ) {
      id
      aum
    }
  }
`;

export const DELETE_PORTFOLIO = gql`
  mutation DeletePortfolio($id: uuid!) {
    delete_EQPortfolio_by_pk(id: $id) {
      id
    }
  }
`;
